import React from 'react';
import { Box, Badge, useTheme, useMediaQuery } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { PanelId, PANEL_LABELS } from '../panelConstants';
import { PANEL_MARKERS } from '../../../features/usage-tracking/usageKeys';

interface NavItem {
  id: PanelId;
  icon: React.ReactNode;
  section?: string;
  isDanger?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'personal', icon: <PersonOutlineIcon />, section: 'Berater' },
  { id: 'contract', icon: <DescriptionOutlinedIcon /> },
  { id: 'finance', icon: <AccountBalanceOutlinedIcon /> },
  { id: 'systems', icon: <SettingsOutlinedIcon /> },
  { id: 'costs', icon: <AttachMoneyOutlinedIcon />, section: 'Verwaltung' },
  { id: 'systemAccess', icon: <LinkOutlinedIcon /> },
  { id: 'docs', icon: <InsertDriveFileOutlinedIcon /> },
  { id: 'notes', icon: <EditNoteOutlinedIcon /> },
  { id: 'dismiss', icon: <WarningAmberOutlinedIcon />, isDanger: true },
];

interface AgentSidebarProps {
  activePanel: PanelId;
  onPanelChange: (panel: PanelId) => void;
  errorCounts: Record<PanelId, number>;
  syncWarningCount?: number;
}

function getItemColor(isActive: boolean, isDanger: boolean, palette: Theme['palette']): string {
  if (isActive) {
    return isDanger ? palette.error.main : palette.primary.main;
  }
  return isDanger ? palette.text.disabled : palette.text.secondary;
}

function getItemBgColor(isActive: boolean, isDanger: boolean, palette: Theme['palette']): string {
  if (!isActive) return 'transparent';
  return isDanger ? alpha(palette.error.main, 0.08) : alpha(palette.primary.main, 0.08);
}

function getHoverBgColor(isActive: boolean, isDanger: boolean, palette: Theme['palette']): string | undefined {
  if (isActive) return undefined;
  return isDanger ? alpha(palette.error.main, 0.06) : alpha(palette.grey[500], 0.06);
}

function getHoverColor(isActive: boolean, isDanger: boolean, palette: Theme['palette']): string | undefined {
  if (isDanger) return palette.error.main;
  if (isActive) return undefined;
  return palette.text.primary;
}

function getIconBgColor(isActive: boolean, isDanger: boolean, palette: Theme['palette']): string {
  if (!isActive) return 'transparent';
  return isDanger ? alpha(palette.error.main, 0.12) : alpha(palette.primary.main, 0.12);
}

function getIconColor(isActive: boolean, isDanger: boolean, palette: Theme['palette']): string {
  if (!isActive) return palette.text.disabled;
  return isDanger ? palette.error.main : palette.primary.main;
}

function AgentSidebar({ activePanel, onPanelChange, errorCounts, syncWarningCount = 0 }: AgentSidebarProps) {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const sidebarWidth = 220;

  return (
    <Box
      component="aside"
      sx={{
        ...(isTablet
          ? {
              width: '100%',
              position: 'sticky',
              top: 0,
              zIndex: 50,
            }
          : {
              width: sidebarWidth,
              flexShrink: 0,
              position: 'sticky',
              top: 24,
              alignSelf: 'flex-start',
            }),
      }}
    >
      <Box
        component="nav"
        sx={{
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 1,
          display: 'flex',
          flexDirection: isTablet ? 'row' : 'column',
          gap: 0.25,
          ...(isTablet && {
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }),
        }}
      >
        {NAV_ITEMS.map((item, index) => {
          const isActive = activePanel === item.id;
          const errCount = errorCounts[item.id] || 0;
          const showSyncBadge = item.id === 'systems' && syncWarningCount > 0 && errCount === 0;

          const showSectionLabel = !isTablet && item.section;
          const showDivider = !isTablet && index > 0 && (item.section === 'Verwaltung' || item.isDanger);

          return (
            <React.Fragment key={item.id}>
              {showSectionLabel && (
                <Box
                  sx={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    color: theme.palette.text.disabled,
                    px: 1.5,
                    pt: index === 0 ? 1 : 1.5,
                    pb: 0.75,
                  }}
                >
                  {item.section}
                </Box>
              )}
              {showDivider && (
                <Box
                  sx={{
                    height: '1px',
                    bgcolor: theme.palette.divider,
                    mx: 1,
                    my: 0.75,
                  }}
                />
              )}
              <Box
                component="button"
                onClick={() => onPanelChange(item.id)}
                data-usage={PANEL_MARKERS[item.id]}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.25,
                  cursor: 'pointer',
                  border: 'none',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  width: isTablet ? 'auto' : '100%',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                  color: getItemColor(isActive, !!item.isDanger, theme.palette),
                  bgcolor: getItemBgColor(isActive, !!item.isDanger, theme.palette),
                  '&:hover': {
                    bgcolor: getHoverBgColor(isActive, !!item.isDanger, theme.palette),
                    color: getHoverColor(isActive, !!item.isDanger, theme.palette),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: getIconBgColor(isActive, !!item.isDanger, theme.palette),
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                      color: getIconColor(isActive, !!item.isDanger, theme.palette),
                    },
                  }}
                >
                  {item.icon}
                </Box>
                {PANEL_LABELS[item.id]}

                {errCount > 0 && (
                  <Badge
                    badgeContent={errCount}
                    sx={{
                      ml: 'auto',
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                        bgcolor: alpha(theme.palette.error.main, 0.15),
                        color: theme.palette.error.main,
                        fontSize: 10,
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        borderRadius: '9px',
                        px: 0.5,
                      },
                    }}
                  />
                )}
                {showSyncBadge && (
                  <Badge
                    badgeContent={syncWarningCount}
                    sx={{
                      ml: 'auto',
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        color: theme.palette.warning.main,
                        fontSize: 10,
                        fontWeight: 700,
                        minWidth: 18,
                        height: 18,
                        borderRadius: '9px',
                        px: 0.5,
                      },
                    }}
                  />
                )}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

export default AgentSidebar;
